import { ChevronLeft, ChevronRight } from "lucide-react";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 30, 50];

type AdminTablePaginationProps = {
  currentPage: number;
  pageSize: number;
  visibleRowCount: number;
  disableNext?: boolean;
  disablePrevious?: boolean;
  isLoading?: boolean;
  pageSizeOptions?: readonly number[];
  onNext: () => void;
  onPrevious: () => void;
  onPageSizeChange: (pageSize: number) => void;
};

export function AdminTablePagination({
  currentPage,
  pageSize,
  visibleRowCount,
  disableNext = false,
  disablePrevious = currentPage === 0,
  isLoading = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onNext,
  onPrevious,
  onPageSizeChange,
}: AdminTablePaginationProps) {
  const firstVisibleRow =
    visibleRowCount === 0 ? 0 : currentPage * pageSize + 1;
  const lastVisibleRow = currentPage * pageSize + visibleRowCount;
  const isPreviousDisabled = disablePrevious || isLoading;
  const isNextDisabled = disableNext || isLoading;

  return (
    <div className="relative flex flex-col items-center gap-3 px-4 py-3 border-t border-slate-200 sm:min-h-16 sm:justify-center">
      <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-slate-500 sm:absolute sm:left-4 sm:top-1/2 sm:-translate-y-1/2 sm:justify-start">
        <span>
          {visibleRowCount === 0
            ? "No results on this page"
            : `Showing ${firstVisibleRow}-${lastVisibleRow}`}
        </span>
        <label className="inline-flex items-center gap-2">
          <span>Rows per page</span>
          <select
            aria-label="Rows per page"
            className="h-8 px-2 text-sm bg-white border rounded-md outline-none border-slate-200 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>
      <nav aria-label="Table pagination" className="flex items-center gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 px-3 text-sm font-medium transition-colors bg-white border rounded-md h-9 border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={isPreviousDisabled}
          onClick={onPrevious}
        >
          <ChevronLeft className="size-4" />
          Previous
        </button>
        <span
          aria-current="page"
          className="inline-flex items-center justify-center px-3 text-sm font-semibold rounded-md bg-slate-100 h-9 min-w-9 text-slate-900"
        >
          {String(currentPage + 1).padStart(2, "0")}
        </span>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-3 text-sm font-semibold transition-colors bg-blue-400 border border-transparent rounded-md h-9 text-slate-900 hover:bg-blue-300 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={isNextDisabled}
          onClick={onNext}
        >
          Next
          <ChevronRight className="size-4" />
        </button>
      </nav>
    </div>
  );
}